import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	signal,
	viewChild,
	afterNextRender
} from '@angular/core';
import { ToastPopoverComponent } from './toast-popover.component';
import { NotificationStackOptions } from './notification/notification-stack.service';

@Component({
	template: `
		<div 
			#stack
			popover="manual" 
			class="notification-stack"
			role="status"
			aria-live="polite"
		>
			<section>
				@for (toast of toasts(); track toast._id; let idx = $index; let count = $count) {
					<skl-toast-popover
						animate.enter="toast-in"
						animate.leave="toast-out"
						[options]="toast"
						[style.--_art-stack-index]="count - 1 - idx"
						[style.--_art-stack-reverse-index]="idx"
					/>
				}
			</section>
		</div>
	`,
	styles: `
		:host {
			--_art-notification-place-items: center;
			--_art-notification-justify-content: center;
			--_art-notification-stack-offset: 2vh;
			--_art-notification-stack-gap: 1vh;

			.notification-stack {
				interpolate-size: allow-keywords;
				border: none;
				background-color: transparent;

				&.stacked:not(:hover) {
					section {
						gap: 0;
					}
					skl-toast {
						transform: scale(calc(1 - (var(--_rev-idx) * 0.05)));
						opacity: calc(1 - (var(--_rev-idx) * 0.1));
						z-index: calc(100 - var(--_rev-idx));
						margin-block-end: calc(min(var(--_rev-idx), 1) * -4rem);
					}
				}

				&:popover-open {
					position: fixed;
					inset: 0;
					width: 100%;
					height: 100%;
					margin: 0;
					overflow: hidden;
					display: flex;
					flex-direction: column;
					flex-wrap: nowrap;
					padding: 0;

					place-items: end;
					justify-content: end;

					pointer-events: none;
					scrollbar-gutter: stable both-edges;
				}
			}

			section {
				interpolate-size: allow-keywords;
				display: grid;
				width: clamp(300px, 50ch, 100%);
				gap: var(--_art-notification-stack-gap);
				padding-block: 0 var(--_art-notification-stack-offset);
				height: auto;
			}

			skl-toast {
				--_idx: var(--_art-stack-index, 0);
				--_rev-idx: var(--_art-stack-reverse-index, 0);

				interpolate-size: allow-keywords;
				opacity: 1;
				order: var(--_art-stack-index, 0);
				transition: 
					opacity 0.35s ease, 
					transform 0.35s ease;

				pointer-events: auto;
			}

			.toast-in {
				animation: toast-in 0.35s ease;
			}

			.toast-out {
				animation: toast-out 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
				pointer-events: none;
				overflow: clip;
			}

			@keyframes toast-in {
				from {
					opacity: 0;
					transform: translateX(100%);
				}
				to {
					opacity: 1;
					transform: translateX(0);
				}
			}

			@keyframes toast-out {
				0% {
					opacity: 1;
					transform: translateX(0);
					height: auto;
					margin-block-end: 0;
				}
				40% {
					opacity: 0;
					transform: translateX(100%);
					height: auto;
					margin-block-end: 0;
				}
				100% {
					opacity: 0;
					transform: translateX(100%);
					height: 0;
					margin-block-end: calc(-1 * var(--_art-notification-stack-gap));
				}
			}
		}

	`,
	imports: [ToastPopoverComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastStackComponent {

	readonly stack = viewChild.required<ElementRef<HTMLElement>>('stack');

	readonly toasts = signal<NotificationStackOptions[]>([]);

	constructor() {
		afterNextRender(() => {
			this.stack().nativeElement.showPopover();
		});
	}
}
